// Considered: RelationshipFilterMolecule — mirrored its row/section IA (add-row
// picker, is/is-not toggle, advanced-only minCount) but rejected as a base: an
// arc row is two-level (an arc SELECTOR × correlated stage/health/lifecycle
// predicates that must all match the SAME arc), which no existing lane molecule
// expresses — relationship rows are one-level edge assertions. See spec Reuse Audit.
//
// Row body extracted to `ArcValueEditorMolecule` (Phase 2, SC2) so
// `FilterEditorPanelMolecule` can render the same single-arc builder for the
// `type==='arc'` property row, without duplicating the correlation logic. This
// lane keeps consuming it for its own list (collapsible header + add/remove).
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import type { ArcFilter } from 'types/filters';
import ArcValueEditorMolecule from '../ArcValueEditor/ArcValueEditorMolecule';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import { nextFilterId, withStableIds } from '../../../utils/ids';
import './ArcFilterMolecule.scss';

export interface ArcFilterMoleculeProps {
  /** Published arc templates the author can see — feeds the fused selector + the stage picker. */
  arcTemplates: ArcTemplateOption[];
  /** Current filter state — the canonical persisted `ArcFilter[]` shape (no editor-only transform). */
  value?: ArcFilter[];
  onChange?: (filters: ArcFilter[]) => void;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  readOnly?: boolean;
  className?: string;
}

const ArcFilterMolecule: React.FC<ArcFilterMoleculeProps> = ({
  arcTemplates,
  value = [],
  onChange,
  collapsible = true,
  defaultCollapsed = true,
  readOnly = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // Normalise once per `value` identity change — never inside the render
  // `.map()` below, which would mint a fresh id (and key) every render.
  const rows = useMemo(() => withStableIds(value), [value]);

  // Without a header there is no affordance to expand, so a non-collapsible
  // section must always render its content — otherwise `defaultCollapsed` would
  // hide the lane permanently.
  const showContent = !collapsible || !collapsed;

  const updateRow = useCallback(
    (index: number, next: ArcFilter) => {
      onChange?.(rows.map((f, i) => (i === index ? next : f)));
    },
    [rows, onChange]
  );

  const removeRow = useCallback(
    (index: number) => {
      onChange?.(rows.filter((_, i) => i !== index));
    },
    [rows, onChange]
  );

  const handleAddRow = useCallback(() => {
    onChange?.([...rows, { id: nextFilterId(), operator: 'in', arcSelector: 'any' }]);
  }, [rows, onChange]);

  const handleClearAll = useCallback(() => {
    onChange?.([]);
  }, [onChange]);

  const activeCount = rows.length;

  return (
    <div className={`arc-filter-molecule ${className}`} data-container="ui-lane-config">
      {collapsible && (
        <button type="button" className="arc-filter-molecule__header" onClick={() => setCollapsed(!collapsed)}>
          <div className="arc-filter-molecule__header-content">
            <i className="pi pi-sitemap" />
            <span className="arc-filter-molecule__title">
              {t('context.editor.arcFilters.title', { defaultValue: 'Arc Filters' })}
            </span>
            {activeCount > 0 && <span className="arc-filter-molecule__badge">{activeCount}</span>}
          </div>
          <i className={`pi ${collapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} />
        </button>
      )}

      {showContent && (
        <div className="arc-filter-molecule__content">
          {/* uf-arc.clear (EAINT-908 Phase 3) — the config panel's "Clear (N)"
              affordance (arc-filter.jsx:421), only meaningful once a row
              exists to clear. */}
          {!readOnly && activeCount > 0 && (
            <div className="arc-filter-molecule__toolbar">
              <button
                type="button"
                className="arc-filter-molecule__clear-btn"
                onClick={handleClearAll}
                data-testid="arc-clear-all"
                data-datum="uf-arc.clear"
                data-kind="button"
              >
                <i className="pi pi-times" aria-hidden="true" />
                {t('context.editor.arcFilters.clearAll', { defaultValue: 'Clear ({{count}})', count: activeCount })}
              </button>
            </div>
          )}
          {rows.map((filter, index) => (
            <div key={filter.id} className="arc-filter-molecule__row" data-container="each(rows)" data-testid={`arc-row-${index}`}>
              <ArcValueEditorMolecule
                arcTemplates={arcTemplates}
                value={filter}
                onChange={(next) => updateRow(index, next)}
                onRemove={() => removeRow(index)}
                readOnly={readOnly}
                index={index}
              />
            </div>
          ))}

          {!readOnly && (
            <div className="arc-filter-molecule__add-row">
              <button
                type="button"
                className="arc-filter-molecule__add-btn"
                onClick={handleAddRow}
                data-testid="arc-add-row"
              >
                <i className="pi pi-plus" aria-hidden="true" />
                {t('context.editor.arcFilters.addArcFilter', { defaultValue: '+ Add arc filter' })}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArcFilterMolecule;
